import React from "react";
import ReactDOM from "react-dom";
import packageJson from '../package.json';
const appVersion = packageJson.version;
import { BrowserRouter as Router } from "react-router-dom";
import { Provider } from "react-redux";
import { createBrowserHistory } from "history";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./index.css";
import App from "./App";
import * as serviceWorker from "./serviceWorker";
import store from "./Redux/store";
import HttpsRedirect from "react-https-redirect";
import * as Sentry from "@sentry/react";
import { BrowserTracing } from "@sentry/tracing";
import LogRocket from "logrocket";
import { fetchLogrocketConfig, fetchSentryConfig } from "./globalConfig";
import { saveLogRocketSession, captureError } from "./utils";

const history = createBrowserHistory();

async function init() {
  // Fetch both configs from the database in parallel
  const [logrocketConfig, sentryConfig] = await Promise.all([
    fetchLogrocketConfig(),
    fetchSentryConfig(),
  ]);

  // Initialize Sentry from database config
  if (sentryConfig.enabled && sentryConfig.dsn) {
    Sentry.init({
      dsn: sentryConfig.dsn,
      environment: sentryConfig.environment || "production",
      release: `sosfront@${appVersion}`,
      tracesSampleRate: sentryConfig.tracesSampleRate ?? 0.05,
      integrations: [new BrowserTracing()],
    });
    window.Sentry = Sentry;
  }

  // Initialize LogRocket from database config
  if (logrocketConfig.appId) {
    LogRocket.init(logrocketConfig.appId);
  }

  ReactDOM.render(
    <>
      <HttpsRedirect>
        <Provider store={store.getInstance()}>
          <Router location={history.location} navigator={history}>
            <Sentry.ErrorBoundary
              fallback={<p>An unexpected error occurred.</p>}
              onError={(error) => {
                captureError(error, { errorType: "react_crash" });
                saveLogRocketSession({ eventType: "error", outcome: "failure", metadata: { error: error?.message } });
              }}
            >
              <App />
            </Sentry.ErrorBoundary>
            <ToastContainer />
          </Router>
        </Provider>
      </HttpsRedirect>
    </>,
    document.getElementById("root")
  );
}

init();

if (window.Cypress) {
  window.store = store.getInstance();
}

// Capture global unhandled errors — associate LogRocket session URL in Sentry context
window.addEventListener("error", (event) => {
  captureError(event.error || new Error(event.message), { errorType: "js_error", context: { filename: event.filename } });
  saveLogRocketSession({ eventType: "error", outcome: "failure", metadata: { message: event.message, filename: event.filename } });
});
window.addEventListener("unhandledrejection", (event) => {
  captureError(event.reason instanceof Error ? event.reason : new Error(String(event.reason)), { errorType: "unhandled_rejection" });
  saveLogRocketSession({ eventType: "error", outcome: "failure", metadata: { reason: String(event.reason) } });
});

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
